import { CircleAlert } from 'lucide-react'
import { type ReactNode, useId } from 'react'
import { cn } from '../cn'

export interface FieldControlProps {
  id: string
  'aria-describedby': string | undefined
  'aria-invalid': true | undefined
  required: boolean | undefined
}

export interface FieldBaseProps {
  label: ReactNode
  hint?: ReactNode
  /** Validation message. Sets `aria-invalid` and is linked with `aria-describedby`. */
  error?: ReactNode
  required?: boolean
  /** Visually hide the label (it stays available to assistive tech). */
  hideLabel?: boolean
  /** Wrapper class. */
  className?: string
  id?: string
}

export interface FieldProps extends FieldBaseProps {
  /** Render prop receiving the props to spread onto the control. */
  children: (control: FieldControlProps) => ReactNode
}

/**
 * Label + hint + error wrapper with programmatic association (UI-050). The input
 * components below use it; custom controls can use it directly through the render prop.
 */
export function Field({
  label,
  hint,
  error,
  required,
  hideLabel,
  className,
  id,
  children,
}: FieldProps) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hintId = hint ? `${fieldId}-hint` : undefined
  const errorId = error ? `${fieldId}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <label
        htmlFor={fieldId}
        className={cn('text-[13px] font-semibold text-teal-800', hideLabel && 'sr-only')}
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-attention">
            *
          </span>
        ) : null}
      </label>
      {children({
        id: fieldId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        required,
      })}
      {hint ? (
        <p id={hintId} className="text-xs leading-relaxed text-sage">
          {hint}
        </p>
      ) : null}
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </div>
  )
}

export function FieldError({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} className="flex items-start gap-1.5 text-[13px] leading-snug text-danger">
      <CircleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  )
}

/** Shared control styling for text-like inputs, selects and textareas. */
export const controlClasses =
  'w-full min-w-0 rounded-md border border-line-strong bg-[#fcfdfb] px-3 text-sm text-ink shadow-[inset_0_1px_1px_rgb(22_62_58/0.04)] transition-[border-color,box-shadow] placeholder:text-sage/80 hover:border-teal-300 focus-visible:border-teal focus-visible:bg-surface focus-visible:shadow-[0_0_0_3px_rgb(42_84_83/0.14)] focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-line-soft disabled:text-sage aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:shadow-[0_0_0_3px_rgb(155_58_36/0.14)]'
